import { Loader2, MessageSquarePlus, Search, Send, WandSparkles } from "lucide-react";
import { useState } from "react";
import { AdminApi } from "@/api";
import type {
  DelegatedAIComposeAcceptedResponse,
  DelegatedAIMessageResponse,
  ProactiveSessionItem,
  ProactiveUserItem,
} from "@/types";
import { Badge, Banner, Button, Card, SelectField, TextField, TextareaField } from "@/components/ui";
import { cn } from "@/lib/utils";

type SendMode = "direct" | "compose";

function sessionTitle(session: ProactiveSessionItem) {
  return session.display_name || session.session_id;
}

function sessionSubtitle(session: ProactiveSessionItem) {
  const parts = [
    session.session_type || "session",
    `${session.member_emails.length} member(s)`,
    session.member_emails.join(", "),
  ].filter(Boolean);
  return parts.join(" · ");
}

function compactId(id: string) {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function ResultPanel({
  result,
}: {
  result: DelegatedAIMessageResponse | DelegatedAIComposeAcceptedResponse | null;
}) {
  if (!result) return null;
  const messageId =
    "message_id" in result ? result.message_id : result.assistant_message_id;
  return (
    <Card className="border-primary/30 bg-primary/5 p-4 text-sm">
      <div className="mb-2 font-semibold">Request accepted</div>
      <div className="grid gap-1 text-muted-foreground">
        <div>mode: {result.authored_via}</div>
        <div>session: {result.session_id}</div>
        <div>message: {messageId}</div>
        {"realtime_topic" in result && <div>topic: {result.realtime_topic}</div>}
      </div>
    </Card>
  );
}

/**
 * Operator send tool: pick a prod session, post an AI message or have the AI
 * compose one. It sits behind the "보내기" tab, with the send log alongside it.
 *
 * Every request goes to SAMS with the SAMS login: the user/session search
 * reads prod through SAMS, and the send goes to SAMS, which relays it to prod
 * Base with its own Base service account. The browser holds no Base
 * credential and never calls Base; until the service account is configured
 * SAMS refuses the send (SAMS_PROACTIVE_BASE_CREDENTIAL_MISSING) and the
 * refusal is shown as it came.
 */
export function ProactiveComposerPanel({
  api,
}: {
  api: AdminApi;
}) {
  const [query, setQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [sessions, setSessions] = useState<ProactiveSessionItem[]>([]);
  const [users, setUsers] = useState<ProactiveUserItem[]>([]);
  const [sendMode, setSendMode] = useState<SendMode>("direct");
  const [message, setMessage] = useState("");
  const [instruction, setInstruction] = useState("");
  const [modelTier, setModelTier] = useState("auto");
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState<DelegatedAIMessageResponse | DelegatedAIComposeAcceptedResponse | null>(null);

  // Without a SAMS address `api` refuses every request (SAMS_API_NOT_CONFIGURED).
  const canSearch = api.hasSamsRuntimeHost;

  const selectedSession = sessions.find((item) => item.session_id === selectedSessionId);

  const canSend =
    api.hasSamsRuntimeHost &&
    selectedSessionId.trim() &&
    targetEmail.trim() &&
    (sendMode === "direct" ? message.trim() : instruction.trim());

  const showError = (err: unknown) => {
    setError(err instanceof Error ? err.message : String(err));
    setNotice("");
  };

  const flash = (text: string) => {
    setNotice(text);
    setError("");
    window.setTimeout(() => setNotice(""), 3000);
  };

  const loadAdminSessions = async () => {
    setLoadingSessions(true);
    setError("");
    try {
      const page = await api.searchProactiveSessions({
        q: query.trim(),
        targetUserEmail: targetEmail.trim(),
        limit: 50,
        // The sessions are sent to on prod Base; SAMS needs prod named.
        environment: "prod",
      });
      setSessions(page.items || []);
      if (!selectedSessionId && page.items?.[0]) {
        setSelectedSessionId(page.items[0].session_id);
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    setError("");
    try {
      // Recipients are prod users; SAMS's copy of Base's search would read dev.
      const page = await api.searchSourceUsers("prod", userQuery.trim(), 50);
      setUsers(page.items || []);
    } catch (err) {
      showError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError("");
    setResult(null);
    try {
      if (sendMode === "direct") {
        const next = await api.postDelegatedAIMessage("prod", selectedSessionId.trim(), {
          target_user_email: targetEmail.trim(),
          message: message.trim(),
          agent_version: 1,
        });
        setResult(next);
        flash("AI message posted.");
      } else {
        const next = await api.composeDelegatedAIMessage("prod", selectedSessionId.trim(), {
          target_user_email: targetEmail.trim(),
          instruction: instruction.trim(),
          agent_version: 1,
          model: modelTier,
        });
        setResult(next);
        flash("AI compose request accepted.");
      }
    } catch (err) {
      showError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-5">
      <p className="text-muted-foreground">
        Select a prod session, then either post a supplied AI message or ask the AI to compose one.
      </p>
      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <Card className="grid gap-4 p-4">
        <div className="text-xs text-muted-foreground">
          사용자/세션 검색과 발송 모두 SAMS로 보냅니다. SAMS가 자기 Base 서비스 계정으로 prod Base에 전달합니다.
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <TextField
            label="Search prod sessions"
            value={query}
            onChange={setQuery}
            placeholder="session name, session id, or member email"
          />
          <Button className="self-end" onClick={loadAdminSessions} disabled={loadingSessions || !canSearch}>
            {loadingSessions ? <Loader2 className="animate-spin" /> : <Search />}
            Load sessions
          </Button>
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <TextField
            label="Search prod users"
            value={userQuery}
            onChange={setUserQuery}
            placeholder="name or email"
          />
          <TextField
            label="Target user email"
            value={targetEmail}
            onChange={setTargetEmail}
            placeholder="hwonkim@too-much-ai.com"
          />
          <Button
            variant="outline"
            className="self-end"
            onClick={loadUsers}
            disabled={loadingUsers || !canSearch}
          >
            {loadingUsers ? <Loader2 className="animate-spin" /> : <Search />}
            Load users
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">
          Admin calls no longer require the operator to be a session member. The target user must still be an active member of the selected session.
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <Card className="min-h-[360px] p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">User and Session Picker</h2>
            <Badge>{sessions.length} session(s)</Badge>
          </div>
          <div className="mb-4 rounded-lg border border-border p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">Prod users</div>
                <div className="text-xs text-muted-foreground">Click a user to fill target_user_email.</div>
              </div>
            </div>
            <div className="flex max-h-32 flex-wrap gap-2 overflow-auto">
              {users.map((user) => (
                <button
                  key={user.email}
                  type="button"
                  onClick={() => setTargetEmail(user.email)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-left text-sm transition-colors",
                    targetEmail.trim().toLowerCase() === user.email.toLowerCase()
                      ? "border-primary bg-primary/8"
                      : "border-border bg-card hover:bg-muted/50",
                  )}
                >
                  <span className="font-medium">{user.display_name || user.email}</span>
                  {user.display_name && <span className="ml-1 text-muted-foreground">{user.email}</span>}
                </button>
              ))}
              {!loadingUsers && users.length === 0 && (
                <div className="text-sm text-muted-foreground">No users loaded yet.</div>
              )}
            </div>
          </div>
          <div className="grid max-h-[520px] gap-2 overflow-auto pr-1">
            {sessions.map((session) => (
              <button
                key={session.session_id}
                type="button"
                onClick={() => setSelectedSessionId(session.session_id)}
                className={cn(
                  "rounded-lg border p-3 text-left transition-colors",
                  selectedSessionId === session.session_id
                    ? "border-primary bg-primary/8"
                    : "border-border bg-card hover:bg-muted/50",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{sessionTitle(session)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{sessionSubtitle(session)}</div>
                  </div>
                  <Badge size="compact">{compactId(session.session_id)}</Badge>
                </div>
              </button>
            ))}
            {!loadingSessions && sessions.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                Load prod sessions. Add a target user email first to filter to sessions that user belongs to.
              </div>
            )}
          </div>
        </Card>

        <Card className="grid gap-4 p-4">
          <div>
            <h2 className="text-xl font-semibold">Message Composer</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Selected session: {selectedSession ? sessionTitle(selectedSession) : "none"}
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setSendMode("direct")}
              className={cn(
                "rounded-lg border p-3 text-left",
                sendMode === "direct" ? "border-primary bg-primary/8" : "border-border",
              )}
            >
              <div className="flex items-center gap-2 font-semibold"><Send className="size-4" /> Direct AI message</div>
              <div className="mt-1 text-xs text-muted-foreground">Post the text exactly as written.</div>
            </button>
            <button
              type="button"
              onClick={() => setSendMode("compose")}
              className={cn(
                "rounded-lg border p-3 text-left",
                sendMode === "compose" ? "border-primary bg-primary/8" : "border-border",
              )}
            >
              <div className="flex items-center gap-2 font-semibold"><WandSparkles className="size-4" /> Ask AI to write</div>
              <div className="mt-1 text-xs text-muted-foreground">The AI composes and posts the proactive message.</div>
            </button>
          </div>
          {sendMode === "direct" ? (
            <TextareaField
              label="Message to post as AI"
              value={message}
              onChange={setMessage}
              rows={10}
            />
          ) : (
            <div className="grid gap-3">
              <TextareaField
                label="Instruction for AI"
                value={instruction}
                onChange={setInstruction}
                rows={10}
              />
              <SelectField
                label="Model tier"
                value={modelTier}
                onChange={setModelTier}
                options={["auto", "minimal", "low", "medium", "high"]}
              />
            </div>
          )}
          {!api.hasSamsRuntimeHost && (
            <div className="text-sm text-muted-foreground">
              SAMS API 주소(VITE_SAMS_API_BASE_URL)가 설정되지 않아 발송할 수 없습니다.
            </div>
          )}
          <Button onClick={send} disabled={sending || !canSend} className="w-fit">
            {sending ? <Loader2 className="animate-spin" /> : <MessageSquarePlus />}
            {sendMode === "direct" ? "Post proactive AI message" : "Compose and post"}
          </Button>
          <ResultPanel result={result} />
        </Card>
      </div>
    </div>
  );
}
